import { useState } from "react";
import { Clapperboard, Copy } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";
import { siteOrigin } from "@/env";
import { roomRecordings, type RoomRecording } from "@/lib/api/rooms";
import { formatDateTime, formatDuration } from "@/lib/format";
import { useI18n } from "@/lib/i18n";

export function RoomRecordings({ count, slug }: { count: number; slug: string }) {
  const [items, setItems] = useState<RoomRecording[] | null>(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { t, locale } = useI18n();

  const load = async () => {
    setLoading(true);
    try {
      setItems(await roomRecordings(slug));
    } catch (error) {
      toast.fail(error);
    } finally {
      setLoading(false);
    }
  };

  const copy = (path: string) => {
    const link = `${siteOrigin()}${path}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.show(t("recordings.linkCopied"), "success");
      })
      .catch(() => {
        toast.show(link, "info");
      });
  };

  if (items === null) {
    return (
      <Button
        disabled={loading}
        onClick={() => {
          void load();
        }}
        size="sm"
        variant="ghost"
      >
        {loading ? <Spinner className="h-4 w-4" /> : <Clapperboard className="h-4 w-4" />}
        {t("recordings.showCount", { count })}
      </Button>
    );
  }

  if (items.length === 0) {
    return <p className="text-xs text-ink-muted">{t("recordings.none")}</p>;
  }

  return (
    <ul className="flex flex-col gap-1">
      {items.map((item) => (
        <li className="flex flex-wrap items-center gap-2 text-xs" key={item.recording.id}>
          <span className="text-ink">{formatDateTime(item.recording.startedAt, locale)}</span>
          <span className="text-ink-muted">
            {formatDuration(item.recording.durationMs / 1000, locale)}
          </span>
          {item.recording.startedByName === null ? null : (
            <span className="text-ink-muted">
              {t("recordings.startedBy", { name: item.recording.startedByName })}
            </span>
          )}
          {item.recording.recording ? (
            <span className="text-danger">{t("recordings.inProgress")}</span>
          ) : null}
          <Button
            onClick={() => {
              copy(item.path);
            }}
            size="sm"
            variant="ghost"
          >
            <Copy className="h-3.5 w-3.5" /> {t("recordings.copyLink")}
          </Button>
          <a
            className="text-brand underline underline-offset-2"
            href={item.path}
            rel="noreferrer"
            target="_blank"
          >
            {t("recordings.watch")}
          </a>
        </li>
      ))}
    </ul>
  );
}
